import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/session'
import { useRoster } from '../lib/roster'
import { silencedEntirely, suppressionLabel } from '../lib/emailrules'
import { badges, area, mlsPrimary } from '../lib/predicates'
import { gmailComposeUrl, gmailLinkTooLong, recheckAtHandoff } from '../lib/campaigns'
import { markDraft } from '../lib/outbox'
import { createTask, deleteTask, toggleTaskDone } from '../lib/tasks'
import { isOverdue, sortTasks } from '../lib/tasksort'
import { deleteBounce, reportBounce } from '../lib/bounces'
import type { Agent, Bounce, OutreachDraft, OutreachLogRow, Task } from '../lib/types'
import Headshot from './Headshot'
import {
  NOT_KNOWN_YET,
  formatDate,
  formatDateTime,
  fullName,
  monthDay,
  triggerLabel,
  yearsWithReal,
} from '../lib/format'

export default function AgentPanel({ agent }: { agent: Agent }) {
  const { appUser } = useAuth()
  const { tagsByAgent, tagsError, reloadTags } = useRoster()

  const [note, setNote] = useState('')
  const [noteLoaded, setNoteLoaded] = useState(false)
  const [noteSavedAt, setNoteSavedAt] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [log, setLog] = useState<OutreachLogRow[]>([])
  const [drafts, setDrafts] = useState<OutreachDraft[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [taskBody, setTaskBody] = useState('')
  const [taskDue, setTaskDue] = useState('')
  const [taskBusy, setTaskBusy] = useState<Set<number>>(new Set())
  const [bounces, setBounces] = useState<Bounce[]>([])
  const [bounceAddress, setBounceAddress] = useState('')
  const [bounceReason, setBounceReason] = useState('')
  const [bounceKind, setBounceKind] = useState<'bounced' | 'complained'>('bounced')
  // Two separate Sets, not one shared — `markBusy` keys on `outreach_drafts.id`
  // (the Sent row being marked), `bounceBusy` keys on `bounces.id` (the row
  // being deleted). Different tables, so the same number can mean two
  // different rows; one shared Set would let marking draft #5 bounced also
  // gray out an unrelated bounce #5's delete button, and vice versa.
  const [markBusy, setMarkBusy] = useState<Set<number>>(new Set())
  const [bounceBusy, setBounceBusy] = useState<Set<number>>(new Set())
  const [reportBusy, setReportBusy] = useState(false)
  const [newTag, setNewTag] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  /**
   * Which skipped cards have been handed to Gmail via Resend — in-memory
   * only, unlike the Outbox's own localStorage-backed tracking
   * (`HANDED_OFF_KEY`). That persistence exists there because a 50-card
   * batch can span days; a single agent's occasional resend does not need
   * to survive closing this panel, and not persisting it means an old,
   * long-forgotten "opened" state can never linger and mislabel a future
   * skip on the same agent.
   */
  const [resendOpened, setResendOpened] = useState<Set<number>>(new Set())
  /**
   * A Set, not a single id — two skipped drafts can both be sitting at "It
   * went" at once, and a scalar here let confirming one silently steal the
   * busy flag from the other mid-flight, flipping its button back to a live,
   * clickable "It went" while its own save was still in the air.
   */
  const [resendBusy, setResendBusy] = useState<Set<number>>(new Set())

  const id = agent.rezen_id
  const tags = tagsByAgent.get(id) ?? []

  /**
   * `AgentPanel` is not remounted between agents — the roster's own list+
   * detail layout swaps the `agent` prop on the same component instance
   * (`Workspace.tsx`). Every task/bounce write below is async: click "Add
   * task" on Agent A, click a different row before it resolves, and without
   * this the write still lands correctly in the database (it's keyed by
   * `id` captured at click time) but its `setTasks`/`setBounces` result
   * would splice Agent A's new row into whatever list is on screen when it
   * resolves — Agent B's. Read, not written, inside the async handlers
   * below: it always holds whichever agent is CURRENTLY showing, so a
   * result that arrives after a switch can tell it is stale and skip
   * applying itself, rather than only skip applying the busy-state cleanup
   * (that part is harmless either way — the finally blocks below still run
   * and just clear a Set entry for a row nothing renders any more).
   */
  const idRef = useRef(id)
  idRef.current = id

  const load = useCallback(async () => {
    setNoteLoaded(false)
    /**
     * Blank the box while the new agent's note is on its way.
     *
     * It used to keep the PREVIOUS agent's text on screen until the fetch
     * landed, and the Save button was live the whole time. Click through the
     * roster quickly, press Save, and one agent's note is written onto
     * another's — with `updated_by` set, which is now the thing that tells the
     * cutover importer never to touch that row again.
     */
    setNote('')
    setNoteSavedAt(null)
    // The manual-report form defaults to this agent's own address — the
    // overwhelmingly common case is reporting a bounce ON the address on
    // file, not some other one. Reset on every agent switch so a half-typed
    // address from the previous agent can never be submitted against this
    // one by mistake.
    setBounceAddress(agent.email ?? '')
    setBounceReason('')
    setBounceKind('bounced')
    const [n, l, d, tk, bn] = await Promise.all([
      supabase.from('agent_notes').select('*').eq('agent_id', id).maybeSingle(),
      supabase.from('outreach_log').select('*').eq('agent_id', id).order('ts', { ascending: false }),
      supabase.from('outreach_drafts').select('*').eq('agent_id', id),
      supabase.from('tasks').select('*').eq('agent_id', id),
      supabase.from('bounces').select('*').eq('agent_id', id).order('detected_at', { ascending: false }),
    ])
    const err = n.error ?? l.error ?? d.error ?? tk.error ?? bn.error
    if (err) {
      setError(err.message)
      return
    }
    setNote(((n.data as { body?: string } | null)?.body) ?? '')
    setNoteSavedAt(((n.data as { updated_at?: string } | null)?.updated_at) ?? null)
    setNoteLoaded(true)
    setLog((l.data ?? []) as OutreachLogRow[])
    setDrafts((d.data ?? []) as OutreachDraft[])
    // Sorted here rather than left in insertion order — 'due' rather than a
    // fresh dropdown of its own, since a single agent's own checklist has
    // exactly one axis worth reading it by: what's next.
    setTasks(sortTasks((tk.data ?? []) as Task[], 'due', () => ''))
    setBounces((bn.data ?? []) as Bounce[])
  }, [id, agent.email])

  useEffect(() => {
    void load()
  }, [load])

  const flash = (m: string) => {
    setToast(m)
    window.setTimeout(() => setToast(null), 1800)
  }

  async function saveNote() {
    // Cannot save what has not arrived. Saving before the load resolves writes
    // an empty note over a real one, authored, and therefore permanent.
    if (!noteLoaded) return
    const startId = id
    setSaving(true)
    const { error } = await supabase.from('agent_notes').upsert(
      {
        agent_id: id,
        body: note,
        updated_by: appUser?.id ?? null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'agent_id' },
    )
    setSaving(false)
    if (error) {
      setError(error.message)
      return
    }
    // Same cross-agent guard as the task/bounce handlers above (see the
    // `idRef` comment near the top of this component) — a save that started
    // on one agent and resolves after the panel has moved on to another
    // must not stamp ITS "last saved" timestamp and toast onto the record
    // now on screen. Found in review: this write already correctly targets
    // `startId` regardless; only the two display-only calls below needed it.
    if (idRef.current !== startId) return
    setNoteSavedAt(new Date().toISOString())
    flash('Note saved')
  }

  async function addTag() {
    const tag = newTag.trim()
    if (!tag) return
    const { error } = await supabase
      .from('agent_tags')
      .insert({ agent_id: id, tag, created_by: appUser?.id ?? null })
    if (error) setError(error.message)
    else {
      setNewTag('')
      reloadTags()
    }
  }

  async function removeTag(tag: string) {
    const { error } = await supabase
      .from('agent_tags')
      .delete()
      .eq('agent_id', id)
      .eq('tag', tag)
    if (error) setError(error.message)
    else reloadTags()
  }

  async function addTask() {
    const { task, error } = await createTask(
      id,
      taskBody,
      taskDue.trim() ? taskDue : null,
      appUser?.id ?? null,
    )
    // Bail if the panel has since moved on to a different agent — see the
    // comment on `idRef` above. The write already landed against the right
    // agent regardless; this only guards what happens to be on screen now.
    if (idRef.current !== id) return
    if (error) {
      setError(error)
      return
    }
    if (task) {
      setTasks((prev) => sortTasks([...prev, task], 'due', () => ''))
      setTaskBody('')
      setTaskDue('')
    }
  }

  /**
   * `taskBusy` is a Set, not a single id — the same reason `resendBusy`
   * above is one: two rows in a checklist can each be mid-toggle at once,
   * and a scalar here would let confirming one silently steal the busy flag
   * from the other.
   */
  async function toggleTask(t: Task) {
    const startId = id
    setTaskBusy((prev) => new Set(prev).add(t.id))
    try {
      const { task, error } = await toggleTaskDone(t)
      if (idRef.current !== startId) return
      if (error) {
        setError(error)
        return
      }
      if (task) setTasks((prev) => sortTasks(prev.map((x) => (x.id === task.id ? task : x)), 'due', () => ''))
    } finally {
      setTaskBusy((prev) => {
        if (!prev.has(t.id)) return prev
        const next = new Set(prev)
        next.delete(t.id)
        return next
      })
    }
  }

  async function removeTask(t: Task) {
    const startId = id
    setTaskBusy((prev) => new Set(prev).add(t.id))
    try {
      const error = await deleteTask(t.id)
      if (idRef.current !== startId) return
      if (error) {
        setError(error)
        return
      }
      setTasks((prev) => prev.filter((x) => x.id !== t.id))
    } finally {
      setTaskBusy((prev) => {
        if (!prev.has(t.id)) return prev
        const next = new Set(prev)
        next.delete(t.id)
        return next
      })
    }
  }

  /** True once this exact Sent draft already has a manual bounce report
   *  against it — the button beneath it becomes a plain "Bounced" label
   *  instead, so a second click can never file a duplicate report for the
   *  same email. */
  const bounceForDraft = (d: OutreachDraft) => bounces.find((b) => b.draft_id === d.id)

  /**
   * The Sent row's own quick action — this is the Reviewed/Gmail lane, the
   * one half of bounce tracking `resend-webhook.ts` can never see into
   * (`gmail-lane.md`). Uses the address the message actually went to
   * (`d.facts.to`, the same field the Resend column shows elsewhere) rather
   * than the agent's current email, so a report made after she's since
   * updated her address on file still records what actually bounced.
   */
  async function markBounced(d: OutreachDraft) {
    if (bounceForDraft(d)) return
    const startId = id
    setMarkBusy((prev) => new Set(prev).add(d.id))
    setError(null)
    try {
      const { bounce, error } = await reportBounce(id, (d.facts?.to as string) ?? agent.email ?? '', {
        subject: d.subject,
        kind: 'bounced',
        draftId: d.id,
        reportedBy: appUser?.id ?? null,
      })
      if (idRef.current !== startId) return
      if (error) {
        setError(error)
        return
      }
      if (bounce) setBounces((prev) => [bounce, ...prev])
    } finally {
      setMarkBusy((prev) => {
        if (!prev.has(d.id)) return prev
        const next = new Set(prev)
        next.delete(d.id)
        return next
      })
    }
  }

  /**
   * The general report form, for the case a Sent row's quick action does
   * not cover — a bounce with no specific email in mind ("her whole domain
   * is down"), or one from before this feature existed with no draft to
   * attach to. Not gated the way `markBounced` is (there is no "already
   * reported this one" to check against a free-typed address).
   */
  async function addBounceReport() {
    const startId = id
    setReportBusy(true)
    setError(null)
    try {
      const { bounce, error } = await reportBounce(id, bounceAddress, {
        kind: bounceKind,
        reason: bounceReason,
        reportedBy: appUser?.id ?? null,
      })
      if (idRef.current !== startId) return
      if (error) {
        setError(error)
        return
      }
      if (bounce) {
        setBounces((prev) => [bounce, ...prev])
        setBounceReason('')
        setBounceKind('bounced')
      }
    } finally {
      setReportBusy(false)
    }
  }

  async function removeBounce(b: Bounce) {
    const startId = id
    setBounceBusy((prev) => new Set(prev).add(b.id))
    try {
      const error = await deleteBounce(b.id)
      if (idRef.current !== startId) return
      if (error) {
        setError(error)
        return
      }
      setBounces((prev) => prev.filter((x) => x.id !== b.id))
    } finally {
      setBounceBusy((prev) => {
        if (!prev.has(b.id)) return prev
        const next = new Set(prev)
        next.delete(b.id)
        return next
      })
    }
  }

  /**
   * Same last-look check the Outbox does before handing anything to Gmail —
   * has this agent left, or been tagged out, since the draft was written?
   * `checkFor` in Outbox.tsx is not exported (it closes over that screen's
   * own `byId`/`tagsByAgent`); this is the same call against this panel's
   * own single `agent` prop instead of a roster lookup.
   */
  const checkFor = (d: OutreachDraft) =>
    recheckAtHandoff({
      agent,
      tags,
      lane: 'reviewed',
      addressOnCard: (d.facts?.to as string) ?? null,
      trigger: d.trigger,
    })

  /**
   * The same tag-freshness gate Outbox.tsx's own `hand`/`copyBody` apply
   * before anything reaches Gmail.
   *
   * Until 2026-08-23 this also checked `sendableFromOutbox` — a skipped
   * milestone (or `welcome`, or a `drip:<uuid>`) draft was exactly the
   * category the 2026-08-21 finding closed off in the Outbox itself, so a
   * second Gmail-handoff path here could have recreated the same
   * double-send-across-two-systems bug through a different door. Removed
   * along with `sendableFromOutbox` itself, per Chris: "Remove the
   * temporary, bring this to full finished ready to use state." See
   * `outbox-milestone-gate-removal-flagged-2026-08-23.md` — that doc named
   * Outbox.tsx's three call sites; this was a fourth one it missed, caught
   * here rather than left half-removed with a call to a function that no
   * longer exists.
   */
  function resendGate(): string | null {
    if (tagsError) {
      return (
        'The agent tags did not load, so this cannot tell who has since asked not to be emailed. ' +
        'Refresh before resending anything. ' + tagsError
      )
    }
    return null
  }

  /**
   * Resend, step 1: hand a skipped draft's stored subject/body to Gmail,
   * exactly as it was written — not re-rendered against the agent's current
   * data, since this is a resend of the specific email that was skipped, not
   * a fresh draft. Mirrors `hand()` in Outbox.tsx; kept separate rather than
   * imported, since that function is written against Outbox's own queue
   * state (handed-off tracking, edit-in-progress body) that this panel does
   * not have and does not need for a single occasional resend.
   */
  function resend(d: OutreachDraft) {
    const gate = resendGate()
    if (gate) {
      setError(gate)
      return
    }
    const check = checkFor(d)
    if (check.block) {
      setError(check.block)
      return
    }
    const url = gmailComposeUrl({ to: check.to ?? '', subject: d.subject, body: d.body })
    if (gmailLinkTooLong(url)) {
      setError(
        'That message is too long to hand to Gmail in one go — Gmail would cut it off. ' +
          'Shorten it in Templates first.',
      )
      return
    }
    const w = window.open(url, `gmail-resend-${d.id}`)
    if (!w) {
      setError('Your browser blocked the Gmail window. Allow pop-ups for this site, then press Resend again.')
      return
    }
    setResendOpened((prev) => new Set(prev).add(d.id))
  }

  /**
   * Resend, step 2: "It went" — same two-step trust pattern as the Outbox,
   * because this app cannot see inside the Gmail window either. Flips the
   * draft (and, via `markDraft`, its `outreach_log` counterpart) from
   * skipped to sent — corrects the existing record rather than creating a
   * new draft, so it can never collide with the `(agent, trigger,
   * occurrence)` uniqueness the way queuing a fresh one could.
   */
  async function confirmResend(d: OutreachDraft) {
    const gate = resendGate()
    if (gate) {
      setError(gate)
      return
    }
    setResendBusy((prev) => new Set(prev).add(d.id))
    setError(null)
    try {
      const check = checkFor(d)
      const facts = { ...(d.facts ?? {}), ...(check.to ? { to: check.to } : {}) }
      const err = await markDraft(d, 'sent', facts, appUser?.id ?? null)
      if (err) {
        setError(err)
        return
      }
      /**
       * `resendBusy`/`resendOpened` stay as they are — button still reads
       * "Saving…" — until `load()` resolves and this row has actually moved
       * out of Skipped. Clearing them first (as an earlier version did) left
       * a window where the row was still rendered under Skipped, now
       * showing a bare "Resend" button again: a stray click there would
       * hand the already-sent message to Gmail a second time.
       */
      await load()
      setResendOpened((prev) => {
        if (!prev.has(d.id)) return prev
        const next = new Set(prev)
        next.delete(d.id)
        return next
      })
      flash('Marked as sent')
    } catch (e) {
      // `load()`/`markDraft` are not expected to throw (Supabase failures
      // resolve with an `.error` field, handled above) — this is a backstop
      // so an unanticipated rejection surfaces as an error instead of
      // leaving the button stuck on "Saving…" forever with no way out.
      setError(e instanceof Error ? e.message : 'Something went wrong saving that. Try again.')
    } finally {
      setResendBusy((prev) => {
        if (!prev.has(d.id)) return prev
        const next = new Set(prev)
        next.delete(d.id)
        return next
      })
    }
  }

  const years = yearsWithReal(agent)
  const anniv = monthDay(agent.anniversary_month, agent.anniversary_day)

  /**
   * The walkthrough's ask: real sub-sections instead of one flat "Drafts"
   * list, so a glance says what actually happened — "Drafts" read as unsent
   * even for the ones that had already gone out.
   */
  const sentEmails = drafts.filter((d) => d.status === 'sent')
  const skippedEmails = drafts.filter((d) => d.status === 'skipped')
  const pendingEmails = drafts.filter((d) => d.status === 'pending')
  const failedEmails = drafts.filter((d) => d.status === 'failed')

  return (
    <div className="panel">
      {error && <p className="error">{error}</p>}

      <div className="phead">
        <Headshot agent={agent} size="detail" />
        <div>
          <h2>
            {fullName(agent)}
            {badges(agent).map((b) => (
              <span key={b.code} className={`badge ${b.code}`}>
                {b.label}
              </span>
            ))}
            {!agent.is_active && <span className="badge dep">DEPARTED</span>}
          </h2>
          <div className="role">
            {[agent.team_name, agent.team_role, area(agent)].filter(Boolean).join(' · ') || '—'}
          </div>
          {!agent.is_active && agent.departed_at && (
            <div className="role">
              Left {formatDate(agent.departed_at)} — history kept.
            </div>
          )}
        </div>
      </div>

      <div className="grid">
        <section className="sec">
          <h3>Contact</h3>
          <Row k="Email" v={agent.email} copy onCopy={flash} />
          <Row k="Real email" v={agent.join_real_email} copy onCopy={flash} />
          <Row k="Phone" v={agent.phone} copy onCopy={flash} />
          <Row k="Home" v={agent.home_address} />
          <Row k="Mailing" v={agent.mailing_address} />
          <Row k="Office" v={agent.office_address} />
        </section>

        <section className="sec">
          <h3>Professional</h3>
          <Row k="Area" v={area(agent)} />
          <Row k="MLS" v={mlsPrimary(agent)} />
          <Row k="State" v={agent.state} />
          <Row k="Licence" v={agent.license_number} />
          <Row k="Licence expires" v={formatDate(agent.license_expires)} />
          <Row k="Service area" v={agent.service_area} />
          <Row k="Pending" v={num(agent.pending_transactions)} />
          <Row k="Closed" v={num(agent.closed_transactions)} />
          <Row k="Active listings" v={num(agent.active_listings)} />
          <Row k="Network" v={num(agent.network_size)} />
        </section>

        <section className="sec">
          <h3>Milestones</h3>
          <Row k="Birthday" v={monthDay(agent.birthday_month, agent.birthday_day)} />
          <Row
            k="Work anniversary"
            v={anniv ? `${anniv}${years != null ? ` · ${years} yrs` : ''}` : null}
          />
          <Row k="Capped" v={formatDate(agent.date_became_capped)} />
          <Row k="Elite" v={formatDate(agent.date_became_elite)} />
          <Row k="First closing" v={formatDate(agent.first_transaction_closed_at)} />
          <Row k="Cap resets" v={formatDate(agent.cap_reset_date)} />
        </section>

        {agent.bio && (
          <section className="sec full">
            <h3>Bio</h3>
            <div className="bio">{agent.bio}</div>
          </section>
        )}

        <section className="sec full notes">
          <h3>Notes</h3>
          <textarea
            value={note}
            disabled={!noteLoaded}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Nothing written yet."
          />
          <div className="savebar">
            {/*
              Disabled until the note has actually arrived. The box is already
              disabled while loading; the button was not, so it was possible to
              save an empty note over a real one before the fetch landed.
            */}
            <button className="btn" onClick={() => void saveNote()} disabled={saving || !noteLoaded}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <span className="status">
              {noteSavedAt ? `Last saved ${formatDateTime(noteSavedAt)}` : 'Never saved'}
            </span>
          </div>
          <div className="hint">
            {/*
              This used to say "Nothing automated ever writes here", which was
              not true: the cutover import populates these from the old
              dashboard's export. It becomes true for a note the moment she
              saves one — from then on the import leaves it alone.
            */}
            Your own words. Once you have saved a note, nothing automatic overwrites it.
          </div>
        </section>

        {/*
          Walkthrough-flagged 2026-08-21: "like Notes, but a checklist."
          Always tied to this one agent — `tasks.agent_id` is `not null`,
          same as Notes and Tags — the cross-agent view is the Tasks tab.
          Sorted 'due' on load and after every write, so the soonest open
          item is always the top row without her having to ask for it.
        */}
        <section className="sec full">
          <h3>Tasks</h3>
          {tasks.length === 0 && <div className="hint">Nothing on the list.</div>}
          {tasks.map((t) => (
            <div className={`task${t.done ? ' done' : ''}`} key={t.id}>
              <label className="chk">
                <input
                  type="checkbox"
                  checked={t.done}
                  disabled={taskBusy.has(t.id)}
                  onChange={() => void toggleTask(t)}
                  aria-label={t.done ? `Mark "${t.body}" not done` : `Mark "${t.body}" done`}
                />
              </label>
              <span className="task-body">{t.body}</span>
              {t.due_date && (
                <span className={isOverdue(t) ? 'task-overdue' : 'hint'}>
                  {isOverdue(t) ? 'Overdue · ' : ''}
                  {formatDate(t.due_date)}
                </span>
              )}
              <button
                className="btn ghost sm"
                onClick={() => void removeTask(t)}
                disabled={taskBusy.has(t.id)}
                aria-label={`Delete task "${t.body}"`}
              >
                Delete
              </button>
            </div>
          ))}
          <div className="addtask">
            <input
              className="task-input"
              value={taskBody}
              placeholder="Add a task"
              onChange={(e) => setTaskBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void addTask()
              }}
            />
            <input
              className="task-date"
              type="date"
              value={taskDue}
              onChange={(e) => setTaskDue(e.target.value)}
              aria-label="Due date (optional)"
            />
            <button className="btn sm" onClick={() => void addTask()} disabled={!taskBody.trim()}>
              Add
            </button>
          </div>
          <div className="hint" style={{ marginTop: 8 }}>
            Checking this tab (or this list) is the only reminder there is — nothing here sends an
            email or a notification.
          </div>
        </section>

        <section className="sec full">
          <h3>Tags</h3>
          {/*
            The two tags that stop email are shown by what they DO, not by the
            name they are stored under. See `SUPPRESSION_LABELS` — the names
            "no-outreach" and "no-bulk-email" both read as more total than they
            are, and acting on the name alone leaves the wrong half running.

            The raw name is kept in the removal title and in the plain line
            underneath, because it is what the roster list, the tag filter, the
            search box and any export still show, and somebody eventually has to
            match the two up.
          */}
          <div className="tags">
            {tags.map((t) => (
              <span className={`tag${suppressionLabel(t) ? ' stop' : ''}`} key={t}>
                {suppressionLabel(t)?.label ?? t}
                <span className="x" onClick={() => void removeTag(t)} title={`Remove ${t}`}>
                  ×
                </span>
              </span>
            ))}
            {tags.length === 0 && <span className="hint">No tags yet.</span>}
          </div>
          {/*
            Both tags together get ONE sentence, not two.

            Each `allows` clause is only true in isolation. Printed side by side
            they contradict each other — "still receives mass emails" directly
            above "still receives one-to-one emails" — on an agent who in fact
            receives nothing at all. And both tags together is not an odd case:
            it is how "do not email me, ever" gets recorded.
          */}
          {silencedEntirely(tags) ? (
            <div className="hint" style={{ marginTop: 6 }}>
              <strong>No emails at all.</strong> This agent is left out of every email this system
              sends — one-to-one and mass alike.{' '}
              <span className="mono">(no-outreach, no-bulk-email)</span>
            </div>
          ) : (
            tags
              .filter((t) => suppressionLabel(t))
              .map((t) => (
                <div className="hint" key={`why-${t}`} style={{ marginTop: 6 }}>
                  <strong>{suppressionLabel(t)?.label}</strong> — {suppressionLabel(t)?.allows}{' '}
                  <span className="mono">({t})</span>
                </div>
              ))
          )}
          <div className="addtag">
            <input
              value={newTag}
              placeholder="Add a tag"
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void addTag()
              }}
            />
            <button className="btn sm" onClick={() => void addTag()} disabled={!newTag.trim()}>
              Add
            </button>
          </div>
        </section>

        <section className="sec full">
          <h3>Outreach history</h3>
          {log.length === 0 && <div className="hint">Nothing recorded.</div>}
          {log.map((r) => (
            <div className="row" key={r.id}>
              <span className="k">
                {triggerLabel(r.trigger)} <span className="hint">· {r.occurrence}</span>
              </span>
              <span className="v">
                <span className={`pill s-${r.status}`}>{r.status}</span>{' '}
                <span className="hint">{formatDateTime(r.ts)}</span>
              </span>
            </div>
          ))}
          {log.length > 0 && (
            <div className="hint" style={{ marginTop: 8 }}>
              <strong>baseline</strong> means the event predates this system — recorded so it
              is never drafted, not something anyone sent.
            </div>
          )}
        </section>

        {drafts.length > 0 && (
          <section className="sec full">
            <h3>Emails</h3>
            <EmailGroup
              title="Email Sent"
              rows={sentEmails}
              defaultOpen
              renderExtra={(d) => {
                const already = bounceForDraft(d)
                return (
                  <div className="savebar" style={{ marginTop: 6 }}>
                    {already ? (
                      <span className="pill s-failed">Bounced {formatDate(already.detected_at)}</span>
                    ) : (
                      <button
                        className="btn ghost sm"
                        disabled={markBusy.has(d.id)}
                        onClick={() => void markBounced(d)}
                      >
                        {markBusy.has(d.id) ? 'Saving…' : 'Mark as bounced'}
                      </button>
                    )}
                    {!already && (
                      <span className="hint">
                        This lane sends through Gmail, which this dashboard cannot see inside — a
                        bounce here has to be reported by hand.
                      </span>
                    )}
                  </div>
                )
              }}
            />
            {/*
              Chris, walkthrough 2026-08-21: "the ask is just to collapse the
              display" for skipped drafts — he wants them kept, not hidden,
              just not sitting open by default the way the old flat "Drafts"
              list left them.
            */}
            <EmailGroup
              title="Email Skipped"
              rows={skippedEmails}
              defaultOpen={false}
              renderExtra={(d) => (
                <div className="savebar" style={{ marginTop: 6 }}>
                  {resendOpened.has(d.id) ? (
                    <button
                      className="btn sm"
                      disabled={resendBusy.has(d.id)}
                      onClick={() => void confirmResend(d)}
                    >
                      {resendBusy.has(d.id) ? 'Saving…' : 'It went'}
                    </button>
                  ) : (
                    <button className="btn sm" onClick={() => resend(d)}>
                      Resend
                    </button>
                  )}
                  <span className="hint">
                    {resendOpened.has(d.id)
                      ? 'Gmail opened with this message. Press "It went" once it is actually sent.'
                      : 'Opens this exact message in Gmail, same as Send does from the Outbox.'}
                  </span>
                </div>
              )}
            />
            <EmailGroup title="Pending Review" rows={pendingEmails} defaultOpen />
            {failedEmails.length > 0 && <EmailGroup title="Failed" rows={failedEmails} defaultOpen />}
            <div className="hint">
              Review only — nothing here sends by itself, except Resend above, which opens Gmail
              the same way Send does and still waits for you to say it went.
            </div>
          </section>
        )}

        {/*
          Walkthrough-flagged 2026-08-21. Every bounce for this agent,
          whatever lane it came from — `source: 'resend'` rows the Bulk-lane
          webhook wrote automatically, `source: 'manual'` rows a person
          reported (either via "Mark as bounced" above, tied to one Sent
          draft, or the free-form report below when there's no one email in
          particular). Always rendered, unlike the Emails section above —
          there is always something to report here even when this agent has
          no drafts at all yet.
        */}
        <section className="sec full">
          <h3>Bounces</h3>
          {bounces.length === 0 && <div className="hint">None reported.</div>}
          {bounces.map((b) => (
            <div className="bounce" key={b.id}>
              <div className="bounce-meta">
                <span className="pill s-failed">{b.kind}</span>
                <span className="pill s-baseline">{b.source === 'resend' ? 'automatic' : 'manual'}</span>
                <span className="bounce-address">{b.address}</span>
              </div>
              {b.subject && <div className="hint">{b.subject}</div>}
              {b.reason && <div className="hint">{b.reason}</div>}
              <div className="bounce-foot">
                <span className="hint">{formatDateTime(b.detected_at)}</span>
                <button
                  className="btn ghost sm"
                  onClick={() => void removeBounce(b)}
                  disabled={bounceBusy.has(b.id)}
                  aria-label={`Delete bounce report for ${b.address}`}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
          <div className="addbounce">
            <input
              className="task-input"
              value={bounceAddress}
              placeholder="Address"
              onChange={(e) => setBounceAddress(e.target.value)}
            />
            <select
              className="areasel-d"
              value={bounceKind}
              onChange={(e) => setBounceKind(e.target.value as 'bounced' | 'complained')}
            >
              <option value="bounced">Bounced</option>
              <option value="complained">Complained</option>
            </select>
            <input
              className="task-input"
              value={bounceReason}
              placeholder="Reason (optional)"
              onChange={(e) => setBounceReason(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void addBounceReport()
              }}
            />
            <button className="btn sm" onClick={() => void addBounceReport()} disabled={reportBusy || !bounceAddress.trim()}>
              {reportBusy ? 'Saving…' : 'Report'}
            </button>
          </div>
          <div className="hint" style={{ marginTop: 8 }}>
            Use this for a bounce with no one Sent email to point at — from the Reviewed lane before
            this feature existed, or a whole domain down rather than one address.
          </div>
        </section>
      </div>

      {toast && <div className="toast show">{toast}</div>}
    </div>
  )
}

const num = (v: number | null) => (v == null ? null : String(v))

/**
 * One sub-section of the Emails section — a collapsible group of drafts all
 * sharing one status. `<details>` rather than component state, because there
 * is nothing to coordinate between groups (each opens and closes on its
 * own) and it keeps the open/closed state out of this component entirely.
 *
 * Always rendered when the caller includes it (`drafts.length > 0` already
 * gates the whole Emails section) — showing "Nothing here yet." for an
 * empty Sent/Skipped/Pending Review group is a real, useful answer ("this
 * agent has never had one skipped"). Failed is the one group the caller
 * skips rendering entirely when empty, per Chris's own parenthetical —
 * failures are rare enough that reserving space for an empty one on every
 * agent would be noise for a case that almost never happens.
 */
function EmailGroup({
  title,
  rows,
  defaultOpen,
  renderExtra,
}: {
  title: string
  rows: OutreachDraft[]
  defaultOpen: boolean
  renderExtra?: (d: OutreachDraft) => ReactNode
}) {
  return (
    <details className="emailgroup" open={defaultOpen}>
      <summary>
        {title} <span className="hint">({rows.length})</span>
      </summary>
      {rows.length === 0 && <div className="hint">Nothing here yet.</div>}
      {rows.map((d) => (
        <div className="entry" key={d.id}>
          <div className="emeta">
            <span>
              {triggerLabel(d.trigger)} · {d.occurrence}
            </span>
            <span className={`pill s-${d.status}`}>{d.status}</span>
          </div>
          {d.why && <div className="hint">{d.why}</div>}
          <div className="dsubject">{d.subject}</div>
          <div className="dbody">{d.body}</div>
          {d.status === 'failed' && d.error && <div className="error">{d.error}</div>}
          {renderExtra?.(d)}
        </div>
      ))}
    </details>
  )
}

function Row({
  k,
  v,
  copy,
  onCopy,
}: {
  k: string
  v: string | null
  copy?: boolean
  onCopy?: (m: string) => void
}) {
  return (
    <div className="row">
      <span className="k">{k}</span>
      <span className="v">
        {v || <span className="hint">{NOT_KNOWN_YET}</span>}
        {copy && v && (
          <button
            className="copy"
            onClick={() => {
              void navigator.clipboard.writeText(v)
              onCopy?.('Copied')
            }}
          >
            copy
          </button>
        )}
      </span>
    </div>
  )
}
